import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import CampaignIcon from '@mui/icons-material/Campaign';
import RefreshIcon from '@mui/icons-material/Refresh';
import PaymentIcon from '@mui/icons-material/Payment';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import SmartphoneIcon from '@mui/icons-material/Smartphone';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import api, { formatApiError } from '../api';
import { useAuth } from '../auth';
import { vendorCurrency } from '../constants';

const DAY_OPTIONS = [1, 3, 7];
const IMPRESSIONS_OPTIONS = [1, 2, 3, 4, 5, 6, 7];
const PROMO_TAGS = [
  'Weekend deal',
  'Discount',
  'Limited time',
  'New arrival',
  'Flash sale',
  'Bundle offer',
  'Clearance',
];
const EXTRA_AED_CENTS_PER_DAY = 100;

const BANNER_DESIGNS = [
  {
    value: 'split',
    label: 'Standard',
    blurb: 'Image left · text beside · normal price',
    premium: false,
  },
  {
    value: 'split_right',
    label: 'Image right',
    blurb: 'Image on the right · +50% price',
    premium: true,
  },
  {
    value: 'typed',
    label: 'Typed text',
    blurb: 'Accent typed title · +50% price',
    premium: true,
  },
];

function formatAmount(cents, currency) {
  if (cents == null || Number.isNaN(Number(cents))) return null;
  const cur = (currency || 'AED').toUpperCase();
  return `${cur} ${(Number(cents) / 100).toFixed(2)}`;
}

function estimateCents(
  impressionsPerDay,
  days,
  design,
  { promoTag, offerDiscountType, showCountdown } = {}
) {
  const daysN = Math.max(1, Number(days) || 1);
  let base = Math.max(100, Number(impressionsPerDay) * daysN * 100);
  const premium = BANNER_DESIGNS.find((d) => d.value === design)?.premium;
  if (premium) base = Math.round(base * 1.5);
  let extras = 0;
  if (promoTag) extras += daysN * EXTRA_AED_CENTS_PER_DAY;
  if (offerDiscountType) extras += daysN * EXTRA_AED_CENTS_PER_DAY;
  if (showCountdown) extras += daysN * EXTRA_AED_CENTS_PER_DAY;
  return base + extras;
}

function campaignStatusLabel(c) {
  const st = String(c?.status_label || c?.status || '').toLowerCase();
  if (st === 'ended' || st === 'completed') return 'completed';
  const ends = c?.ends_at ? new Date(c.ends_at).getTime() : NaN;
  if (
    (st === 'active' || st === 'paused') &&
    Number.isFinite(ends) &&
    ends < Date.now()
  ) {
    return 'completed';
  }
  return st || 'draft';
}

function TypedPreviewTitle({ text, typed }) {
  const [shown, setShown] = useState('');
  useEffect(() => {
    if (!typed) {
      setShown(text || '');
      return undefined;
    }
    setShown('');
    const full = text || 'Your product title';
    let i = 0;
    const timer = window.setInterval(() => {
      i += 1;
      setShown(full.slice(0, i));
      if (i >= full.length) window.clearInterval(timer);
    }, 42);
    return () => window.clearInterval(timer);
  }, [text, typed]);
  if (!typed) {
    return (
      <Typography
        sx={{
          fontSize: 11,
          fontWeight: 800,
          lineHeight: 1.2,
          color: '#173d3b',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {text || 'Your product title'}
      </Typography>
    );
  }
  return (
    <Typography
      sx={{
        fontSize: 11,
        fontWeight: 800,
        lineHeight: 1.2,
        color: '#0f766e',
        fontStyle: 'italic',
        letterSpacing: '0.02em',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
      }}
    >
      {shown}
      {shown.length < (text || 'Your product title').length ? (
        <Box
          component="span"
          sx={{
            display: 'inline-block',
            width: 2,
            height: '0.9em',
            bgcolor: '#0f766e',
            ml: 0.25,
            verticalAlign: 'text-bottom',
            animation: 'pulse 1s ease-in-out infinite',
          }}
        />
      ) : null}
    </Typography>
  );
}

function NeighbourPhonePreview({
  title,
  imageUrl,
  design,
  promoTag,
  offerLabel,
  showCountdown,
  days,
}) {
  const d = BANNER_DESIGNS.some((x) => x.value === design) ? design : 'split';
  const imageFirst = d !== 'split_right';
  const typed = d === 'typed';

  const thumb = imageUrl ? (
    <Box
      component="img"
      src={imageUrl}
      alt=""
      sx={{
        width: 44,
        height: 44,
        objectFit: 'cover',
        flexShrink: 0,
        bgcolor: '#e8f5f2',
      }}
    />
  ) : (
    <Box
      sx={{
        width: 44,
        height: 44,
        flexShrink: 0,
        bgcolor: '#26aea3',
      }}
    />
  );

  return (
    <Box sx={{ width: '100%', maxWidth: 300 }}>
      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 1 }}>
        <SmartphoneIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
        <Typography variant="subtitle2" fontWeight={700}>
          Neighbour preview (mobile)
        </Typography>
      </Stack>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
        How your ad appears on resident phones inside GoSnel Neighbour
      </Typography>
      <Box
        sx={{
          width: 280,
          mx: 'auto',
          borderRadius: '28px',
          border: '10px solid #1f2937',
          boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
          overflow: 'hidden',
          bgcolor: '#1f2937',
        }}
      >
        <Box sx={{ height: 22, display: 'flex', justifyContent: 'center', pt: 0.5 }}>
          <Box sx={{ width: 72, height: 5, borderRadius: 3, bgcolor: '#374151' }} />
        </Box>
        <Box sx={{ position: 'relative', height: 460, bgcolor: '#f7faf9', overflow: 'hidden' }}>
          <Box
            sx={{
              height: 44,
              borderBottom: '1px solid #e4efec',
              bgcolor: '#fff',
              display: 'flex',
              alignItems: 'center',
              px: 1.25,
              gap: 1,
            }}
          >
            <Box sx={{ width: 26, height: 26, borderRadius: 1.5, bgcolor: '#c8ebe5' }} />
            <Box sx={{ flex: 1, height: 9, borderRadius: 1, bgcolor: '#e4efec', maxWidth: 110 }} />
          </Box>
          <Box sx={{ p: 1.25 }}>
            <Box sx={{ height: 10, width: '65%', bgcolor: '#e4efec', borderRadius: 1, mb: 1 }} />
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 1,
              }}
            >
              {[1, 2, 3, 4].map((n) => (
                <Box
                  key={n}
                  sx={{
                    height: 58,
                    borderRadius: 1.5,
                    bgcolor: '#fff',
                    border: '1px solid #e4efec',
                    p: 1,
                  }}
                >
                  <Box sx={{ width: 22, height: 22, borderRadius: 1, bgcolor: '#eef6f4', mb: 0.5 }} />
                  <Box sx={{ height: 6, width: '75%', bgcolor: '#e4efec', borderRadius: 0.5 }} />
                </Box>
              ))}
            </Box>
          </Box>

          <Box
            sx={{
              position: 'absolute',
              left: 10,
              right: 10,
              top: 56,
              display: 'flex',
              alignItems: 'stretch',
              minHeight: 56,
              borderRadius: 2,
              overflow: 'hidden',
              border: '1px solid #c8ebe5',
              bgcolor: '#fff',
              boxShadow: '0 2px 10px rgba(25,78,72,0.08)',
            }}
          >
            {imageFirst ? thumb : null}
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                px: 1,
                py: 0.75,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
              }}
            >
              {promoTag ? (
                <Chip
                  size="small"
                  label={promoTag}
                  sx={{
                    height: 18,
                    mb: 0.35,
                    alignSelf: 'flex-start',
                    fontSize: 9,
                    fontWeight: 800,
                    bgcolor: '#fff7ed',
                    color: '#c2410c',
                  }}
                />
              ) : null}
              <TypedPreviewTitle text={title || 'Your product title'} typed={typed} />
              {offerLabel ? (
                <Typography sx={{ fontSize: 10, fontWeight: 800, color: '#0f766e', mt: 0.25 }}>
                  {offerLabel}
                </Typography>
              ) : null}
              {showCountdown ? (
                <Typography sx={{ fontSize: 9, fontWeight: 700, color: '#b45309', mt: 0.25 }}>
                  Ends in {days} day{days === 1 ? '' : 's'}
                </Typography>
              ) : (
                <Typography
                  sx={{
                    fontSize: 9,
                    color: '#648782',
                    mt: 0.25,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Shown to residents in your delivery radius
                </Typography>
              )}
            </Box>
            {!imageFirst ? thumb : null}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default function AdsPage() {
  const { vendor } = useAuth();
  const currency = vendorCurrency(vendor);
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [search, setSearch] = useState('');
  const [productIds, setProductIds] = useState([]);
  const [adTitle, setAdTitle] = useState('');
  const [titleTouched, setTitleTouched] = useState(false);
  const [impressionsPerDay, setImpressionsPerDay] = useState(3);
  const [days, setDays] = useState(3);
  const [design, setDesign] = useState('split');
  const [promoTag, setPromoTag] = useState('');
  const [offerEnabled, setOfferEnabled] = useState(false);
  const [offerDiscountType, setOfferDiscountType] = useState('percent');
  const [offerDiscountValue, setOfferDiscountValue] = useState('');
  const [showCountdown, setShowCountdown] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [payingId, setPayingId] = useState(null);
  const [actionId, setActionId] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [apiMissing, setApiMissing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [prodRes, adsRes] = await Promise.all([
        api.get('/api/vendor/products', { params: { include_inactive: false } }),
        api.get('/api/towers/shop/ads').catch((err) => {
          if (err?.response?.status === 404) {
            setApiMissing(true);
            return { data: [] };
          }
          throw err;
        }),
      ]);
      setProducts(prodRes.data?.products || []);
      const list = Array.isArray(adsRes.data)
        ? adsRes.data
        : adsRes.data?.campaigns || adsRes.data?.ads || [];
      setCampaigns(list);
      if (adsRes.data !== undefined) {
        setApiMissing(false);
      }
    } catch (err) {
      if (err?.response?.status === 404) {
        setApiMissing(true);
      } else {
        setError(formatApiError(err, 'Could not load ads'));
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const adPaid = searchParams.get('ad_paid');
    const adCanceled = searchParams.get('ad_canceled');
    const campaignId = searchParams.get('campaign_id');
    const sessionId = searchParams.get('session_id');

    if (adCanceled) {
      setSuccess('');
      setError('Checkout canceled. Your campaign is still a draft — pay when ready, or delete it.');
      setSearchParams({}, { replace: true });
      return;
    }

    if (!adPaid || !campaignId || !sessionId) return;

    let cancelled = false;
    (async () => {
      setSaving(true);
      setError('');
      try {
        await api.post(`/api/towers/shop/ads/${campaignId}/confirm`, null, {
          params: { session_id: sessionId },
        });
        if (!cancelled) {
          setSuccess('Payment confirmed. Your ad is now active on Neighbour banners.');
          await load();
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            formatApiError(
              err,
              'Payment received but confirmation failed. Refresh in a moment — webhook may still activate it.'
            )
          );
          await load();
        }
      } finally {
        if (!cancelled) {
          setSaving(false);
          setSearchParams({}, { replace: true });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [searchParams, setSearchParams, load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return products;
    return products.filter(
      (p) =>
        String(p.name || '')
          .toLowerCase()
          .includes(q) ||
        String(p.category || '')
          .toLowerCase()
          .includes(q)
    );
  }, [products, search]);

  const selectedProducts = useMemo(
    () =>
      productIds
        .map((id) => products.find((p) => String(p.id) === String(id)))
        .filter(Boolean),
    [productIds, products]
  );
  const selected = useMemo(() => {
    if (!selectedProducts.length) return null;
    const withImg = selectedProducts.filter((p) => p.image_url);
    const pool = withImg.length ? withImg : selectedProducts;
    return pool.reduce((best, p) => {
      const bp = Number(best?.price_aed);
      const mp = Number(p.price_aed);
      const bv = Number.isFinite(bp) ? bp : -1;
      const mv = Number.isFinite(mp) ? mp : -1;
      return mv > bv ? p : best;
    }, pool[0]);
  }, [selectedProducts]);
  const autoTitle = selectedProducts.map((p) => p.name).filter(Boolean).join(' + ');
  const displayTitle = titleTouched ? adTitle : autoTitle;

  useEffect(() => {
    if (!titleTouched) setAdTitle(autoTitle);
  }, [autoTitle, titleTouched]);

  const toggleProductId = (id) => {
    const sid = String(id);
    setProductIds((prev) => {
      if (prev.some((x) => String(x) === sid)) {
        return prev.filter((x) => String(x) !== sid);
      }
      if (prev.length >= 5) return prev;
      return [...prev, id];
    });
  };

  const estimatedImpressions = impressionsPerDay * days;
  const activeOfferType = offerEnabled ? offerDiscountType : '';
  const estimatedPriceCents = estimateCents(impressionsPerDay, days, design, {
    promoTag,
    offerDiscountType: activeOfferType,
    showCountdown,
  });
  const designMeta = BANNER_DESIGNS.find((d) => d.value === design);
  const extraCount = [promoTag, activeOfferType, showCountdown].filter(Boolean).length;
  const previewOfferLabel = (() => {
    if (!offerEnabled || !offerDiscountValue) return null;
    const n = Number(offerDiscountValue);
    if (!Number.isFinite(n) || n <= 0) return null;
    if (offerDiscountType === 'percent') return `${n}% off`;
    return `${currency} ${n.toFixed(2)} off`;
  })();

  const historyStats = useMemo(() => {
    const started = campaigns.filter((c) =>
      ['active', 'paused', 'ended'].includes(String(c.status || '').toLowerCase())
    ).length;
    const impressions = campaigns.reduce((n, c) => n + (Number(c.impressions_count) || 0), 0);
    const clicks = campaigns.reduce((n, c) => n + (Number(c.clicks_count) || 0), 0);
    return { total: campaigns.length, started, impressions, clicks };
  }, [campaigns]);

  const createCampaign = async () => {
    if (!productIds.length) {
      setError('Select at least one product to promote');
      return;
    }
    if (productIds.length > 5) {
      setError('Combo ads can include up to 5 products.');
      return;
    }
    if (!selected?.image_url) {
      setError('The first selected product needs a photo — it becomes the banner image.');
      return;
    }
    if (offerEnabled) {
      const n = Number(offerDiscountValue);
      if (!Number.isFinite(n) || n <= 0) {
        setError('Enter a discount amount, or turn off the offer add-on.');
        return;
      }
      if (offerDiscountType === 'percent' && n > 100) {
        setError('Percent discount cannot exceed 100.');
        return;
      }
    }
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const titlePayload = (displayTitle || '').trim();
      const res = await api.post('/api/towers/shop/ads', {
        product_ids: productIds,
        title: titlePayload || undefined,
        impressions_per_day: impressionsPerDay,
        days,
        design,
        promo_tag: promoTag || null,
        offer_discount_type: offerEnabled ? offerDiscountType : null,
        offer_discount_value: offerEnabled ? Number(offerDiscountValue) : null,
        show_countdown: Boolean(showCountdown),
      });
      const camp = res.data?.ad || res.data?.campaign || res.data;
      setSuccess(
        camp?.id
          ? productIds.length > 1
            ? 'Combo campaign created. Pay with Stripe or from wallet to go live on Neighbour banners.'
            : 'Campaign created. Pay with Stripe or from wallet to go live on Neighbour banners.'
          : 'Campaign created. Complete payment to activate.'
      );
      setApiMissing(false);
      setProductIds([]);
      setAdTitle('');
      setTitleTouched(false);
      await load();
    } catch (err) {
      if (err?.response?.status === 404) {
        setApiMissing(true);
        setError('Promote / Ads API is not live yet. Your selection is ready for when it ships.');
      } else {
        setError(formatApiError(err, 'Could not create campaign'));
      }
    } finally {
      setSaving(false);
    }
  };

  const payCampaign = async (campaignId) => {
    setPayingId(campaignId);
    setError('');
    setSuccess('');
    try {
      const res = await api.post(`/api/towers/shop/ads/${campaignId}/checkout`);
      const url = res.data?.checkout_url;
      if (!url) {
        setError('Checkout URL missing from server response.');
        return;
      }
      window.location.href = url;
    } catch (err) {
      setError(formatApiError(err, 'Could not start Stripe checkout'));
      setPayingId(null);
    }
  };

  const payFromWallet = async (campaignId) => {
    setPayingId(`wallet-${campaignId}`);
    setError('');
    setSuccess('');
    try {
      await api.post(`/api/towers/shop/ads/${campaignId}/pay-wallet`);
      setSuccess('Paid from wallet. Your ad is now active on Neighbour banners.');
      await load();
    } catch (err) {
      setError(formatApiError(err, 'Could not pay from wallet'));
    } finally {
      setPayingId(null);
    }
  };

  const runAction = async (campaignId, action) => {
    const labels = {
      delete: 'Delete this draft / pending campaign?',
      cancel: 'Cancel this pending payment campaign?',
      pause: 'Pause this active ad? Residents will stop seeing it.',
      resume: 'Resume this paused ad?',
      stop: 'Stop this ad early? It will end and cannot resume.',
    };
    if (labels[action] && !window.confirm(labels[action])) return;
    setActionId(`${action}-${campaignId}`);
    setError('');
    setSuccess('');
    try {
      if (action === 'delete') {
        await api.delete(`/api/towers/shop/ads/${campaignId}`);
      } else {
        await api.post(`/api/towers/shop/ads/${campaignId}/action`, { action });
      }
      setSuccess(
        action === 'delete'
          ? 'Campaign deleted.'
          : action === 'cancel'
            ? 'Campaign cancelled.'
            : action === 'pause'
              ? 'Ad paused.'
              : action === 'resume'
                ? 'Ad resumed.'
                : 'Ad stopped.'
      );
      await load();
    } catch (err) {
      setError(formatApiError(err, `Could not ${action} campaign`));
    } finally {
      setActionId(null);
    }
  };

  const canPay = (c) => {
    const st = String(c.status || '').toLowerCase();
    return st === 'draft' || st === 'pending_payment';
  };

  const canDelete = (c) => {
    const st = String(c.status || '').toLowerCase();
    return st === 'draft' || st === 'pending_payment';
  };

  if (loading) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ sm: 'center' }}
        spacing={1.5}
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="h5" fontWeight={900}>
            Promote / Ads
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Feature a product on GoSnel Neighbour. Ads reach residents within your shop delivery
            radius (not only linked buildings). Pay with Stripe or from your prepaid wallet.
          </Typography>
        </Box>
        <Button startIcon={<RefreshIcon />} onClick={load}>
          Refresh
        </Button>
      </Stack>

      <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
        <Typography fontWeight={800} sx={{ mb: 0.5 }}>
          See your ads live
        </Typography>
        Ads run by your delivery radius on user.gosnel.com. Create a resident account for a
        building inside your radius, open Neighbour home, and your active banner appears there.
        Radius is set in Shop profile / Settings (0.5–1.5 km).
      </Alert>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 2 }} useFlexGap flexWrap="wrap">
        {[
          { label: 'Campaigns', value: historyStats.total },
          { label: 'Started', value: historyStats.started },
          { label: 'Impressions', value: historyStats.impressions },
          { label: 'Clicks', value: historyStats.clicks },
        ].map((s) => (
          <Paper key={s.label} variant="outlined" sx={{ px: 1.75, py: 1, borderRadius: 2, minWidth: 110 }}>
            <Typography variant="caption" color="text.secondary">
              {s.label}
            </Typography>
            <Typography fontWeight={900}>{s.value}</Typography>
          </Paper>
        ))}
      </Stack>

      {apiMissing && (
        <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
          Ads campaigns API is not live yet. You can still pick a product and settings — create will
          work after the backend endpoint ships.
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} alignItems="flex-start" sx={{ mb: 2 }}>
        <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, flex: 1, width: '100%' }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
            <CampaignIcon color="primary" />
            <Typography fontWeight={800}>New campaign</Typography>
          </Stack>
          <Stack spacing={2}>
            <TextField
              label="Search products"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              fullWidth
              placeholder="Name or category"
            />
            <FormControl fullWidth>
              <InputLabel id="ad-product-label">Products (1–5)</InputLabel>
              <Select
                labelId="ad-product-label"
                label="Products (1–5)"
                multiple
                value={productIds}
                onChange={(e) => {
                  const raw = e.target.value;
                  const next = typeof raw === 'string' ? raw.split(',') : raw;
                  const nextSet = new Set(next.map(String));
                  const kept = productIds.filter((id) => nextSet.has(String(id)));
                  const added = next.filter((id) => !productIds.some((p) => String(p) === String(id)));
                  setProductIds([...kept, ...added].slice(0, 5));
                }}
                renderValue={(selectedVals) => {
                  if (!selectedVals?.length) return 'Select products';
                  const names = selectedVals
                    .map((id) => products.find((x) => String(x.id) === String(id))?.name)
                    .filter(Boolean);
                  return names.join(' + ') || `${selectedVals.length} selected`;
                }}
              >
                {filtered.map((p) => {
                  const checked = productIds.some((id) => String(id) === String(p.id));
                  const atMax = productIds.length >= 5 && !checked;
                  return (
                    <MenuItem key={p.id} value={p.id} disabled={atMax} sx={{ py: 1.25 }}>
                      <Checkbox checked={checked} sx={{ mr: 0.5 }} tabIndex={-1} disableRipple />
                      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ width: '100%' }}>
                        <Box
                          component={p.image_url ? 'img' : 'div'}
                          src={p.image_url || undefined}
                          alt=""
                          sx={{
                            width: 48,
                            height: 48,
                            borderRadius: 1.5,
                            objectFit: 'cover',
                            bgcolor: '#e8f5f2',
                            flexShrink: 0,
                            border: '1px solid #d5ebe6',
                          }}
                        />
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography fontWeight={800} noWrap>
                            {p.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {p.price_display
                              ? p.price_display
                              : p.price_aed != null
                                ? `${currency} ${Number(p.price_aed).toFixed(2)}`
                                : p.category || 'Product'}
                            {checked &&
                            selected &&
                            String(selected.id) === String(p.id)
                              ? ' · Banner photo'
                              : ''}
                          </Typography>
                        </Box>
                      </Stack>
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
            {selectedProducts.length > 0 && (
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {selectedProducts.map((p) => (
                  <Chip
                    key={p.id}
                    size="small"
                    label={
                      selected && String(selected.id) === String(p.id)
                        ? `${p.name} · banner`
                        : p.name
                    }
                    onDelete={() => toggleProductId(p.id)}
                    sx={{ fontWeight: 700 }}
                  />
                ))}
              </Stack>
            )}
            <Typography variant="caption" color="text.secondary">
              Select 2–5 products for a combo. Banner photo is the most expensive product that has a photo.
            </Typography>
            <TextField
              label="Ad title"
              value={displayTitle}
              onChange={(e) => {
                setTitleTouched(true);
                setAdTitle(e.target.value);
              }}
              fullWidth
              placeholder="Auto from product names"
              helperText={
                selectedProducts.length > 1
                  ? 'Combo title — edit if you want (e.g. Weekend basket)'
                  : 'Optional — defaults to the product name'
              }
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <FormControl fullWidth>
                <InputLabel id="imp-label">Impressions / day</InputLabel>
                <Select
                  labelId="imp-label"
                  label="Impressions / day"
                  value={impressionsPerDay}
                  onChange={(e) => setImpressionsPerDay(Number(e.target.value))}
                >
                  {IMPRESSIONS_OPTIONS.map((n) => (
                    <MenuItem key={n} value={n}>
                      {n} / day
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel id="days-label">Duration</InputLabel>
                <Select
                  labelId="days-label"
                  label="Duration"
                  value={days}
                  onChange={(e) => setDays(Number(e.target.value))}
                >
                  {DAY_OPTIONS.map((n) => (
                    <MenuItem key={n} value={n}>
                      {n} day{n > 1 ? 's' : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>

            <Box>
              <Typography fontWeight={800} sx={{ mb: 0.75 }}>
                Banner design
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                Compact fixed image size with text beside it. Standard is normal price; premium
                layouts cost 50% more.
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                {BANNER_DESIGNS.map((d) => {
                  const selectedDesign = design === d.value;
                  return (
                    <Paper
                      key={d.value}
                      variant="outlined"
                      onClick={() => setDesign(d.value)}
                      sx={{
                        flex: 1,
                        p: 1.25,
                        borderRadius: 2,
                        cursor: 'pointer',
                        borderColor: selectedDesign ? 'primary.main' : 'divider',
                        borderWidth: selectedDesign ? 2 : 1,
                        bgcolor: selectedDesign ? 'rgba(15,118,110,0.06)' : 'background.paper',
                      }}
                    >
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography fontWeight={800} fontSize={13}>
                          {d.label}
                        </Typography>
                        <Chip
                          size="small"
                          label={d.premium ? '+50%' : 'Standard'}
                          color={d.premium ? 'warning' : 'success'}
                          sx={{ height: 22, fontWeight: 700 }}
                        />
                      </Stack>
                      <Typography variant="caption" color="text.secondary">
                        {d.blurb}
                      </Typography>
                    </Paper>
                  );
                })}
              </Stack>
            </Box>

            <Box>
              <Typography fontWeight={800} sx={{ mb: 0.5 }}>
                Optional add-ons
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                Each add-on is +{currency} 1.00 per day for the duration you choose.
              </Typography>
              <Stack spacing={1.25}>
                <FormControl fullWidth size="small">
                  <InputLabel id="promo-tag-label">Promo tag</InputLabel>
                  <Select
                    labelId="promo-tag-label"
                    label="Promo tag"
                    value={promoTag}
                    onChange={(e) => setPromoTag(e.target.value)}
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {PROMO_TAGS.map((tag) => (
                      <MenuItem key={tag} value={tag}>
                        {tag} · +{currency} {(days * 1).toFixed(2)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={offerEnabled}
                      onChange={(e) => setOfferEnabled(e.target.checked)}
                    />
                  }
                  label={`Offer discount (+${currency} ${(days * 1).toFixed(2)})`}
                />
                {offerEnabled ? (
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                    <FormControl fullWidth size="small">
                      <InputLabel id="offer-type-label">Discount type</InputLabel>
                      <Select
                        labelId="offer-type-label"
                        label="Discount type"
                        value={offerDiscountType}
                        onChange={(e) => setOfferDiscountType(e.target.value)}
                      >
                        <MenuItem value="percent">Percent %</MenuItem>
                        <MenuItem value="fixed">{currency} amount</MenuItem>
                      </Select>
                    </FormControl>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      label={offerDiscountType === 'percent' ? 'Percent off' : `${currency} off`}
                      value={offerDiscountValue}
                      onChange={(e) => setOfferDiscountValue(e.target.value)}
                      inputProps={{ min: 0, max: offerDiscountType === 'percent' ? 100 : undefined, step: '0.01' }}
                    />
                  </Stack>
                ) : null}

                <FormControlLabel
                  control={
                    <Switch
                      checked={showCountdown}
                      onChange={(e) => setShowCountdown(e.target.checked)}
                    />
                  }
                  label={`Countdown timer until offer ends (+${currency} ${(days * 1).toFixed(2)})`}
                />
              </Stack>
            </Box>

            <Alert severity="info" sx={{ borderRadius: 2 }}>
              Estimated ~{estimatedImpressions} banner impressions
              {displayTitle ? ` for “${displayTitle}”` : ''}
              {selectedProducts.length > 1 ? ` · combo (${selectedProducts.length})` : ''}
              {designMeta?.premium ? ' · premium design' : ' · standard design'}
              {extraCount ? ` · ${extraCount} add-on${extraCount === 1 ? '' : 's'}` : ''} · from{' '}
              {formatAmount(estimatedPriceCents, currency) || `${currency} —`}. Reach covers
              residents within your shop delivery radius.
            </Alert>
            <Button
              variant="contained"
              onClick={createCampaign}
              disabled={saving || !productIds.length}
              sx={{ alignSelf: 'flex-start' }}
            >
              {saving ? <CircularProgress size={20} color="inherit" /> : 'Create campaign'}
            </Button>
          </Stack>
        </Paper>

        <Box sx={{ width: { xs: '100%', lg: 320 }, flexShrink: 0 }}>
          <NeighbourPhonePreview
            title={displayTitle || 'Your product title'}
            imageUrl={selected?.image_url}
            design={design}
            promoTag={promoTag || null}
            offerLabel={previewOfferLabel}
            showCountdown={showCountdown}
            days={days}
          />
        </Box>
      </Stack>

      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
        <Typography fontWeight={800} sx={{ mb: 1.5 }}>
          Your campaigns (history)
        </Typography>
        {campaigns.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No campaigns yet. Create one above to promote a product on Neighbour.
          </Typography>
        ) : (
          <Stack spacing={1.25}>
            {campaigns.map((c) => {
              const st = campaignStatusLabel(c);
              const rawStatus = String(c.status || '').toLowerCase();
              return (
                <Paper key={c.id} variant="outlined" sx={{ p: 1.75, borderRadius: 2 }}>
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    justifyContent="space-between"
                    spacing={1}
                    alignItems={{ sm: 'center' }}
                  >
                    <Box>
                      <Typography fontWeight={800}>
                        {c.product_name || c.title || c.name || `Campaign ${c.id}`}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {[
                          c.impressions_per_day != null ? `${c.impressions_per_day}/day` : null,
                          c.days != null ? `${c.days} days` : null,
                          formatAmount(c.amount_cents, c.currency || currency),
                          BANNER_DESIGNS.find((d) => d.value === c.design)?.label || c.design,
                          c.promo_tag || null,
                          c.offer_discount_type
                            ? c.offer_discount_type === 'percent'
                              ? `${c.offer_discount_value}% off`
                              : `${c.currency || currency} ${Number(c.offer_discount_value || 0).toFixed(2)} off`
                            : null,
                          c.show_countdown ? 'countdown' : null,
                          c.payment_method,
                          st,
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                        Views {c.impressions_count ?? 0} · Clicks {c.clicks_count ?? 0}
                        {c.starts_at ? ` · Started ${String(c.starts_at).slice(0, 10)}` : ''}
                        {c.ends_at ? ` · Ends ${String(c.ends_at).slice(0, 10)}` : ''}
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                      {(() => {
                        let combo = c.combo_items;
                        if (typeof combo === 'string') {
                          try {
                            combo = JSON.parse(combo);
                          } catch {
                            combo = null;
                          }
                        }
                        if (Array.isArray(combo) && combo.length > 1) {
                          return (
                            <Chip
                              size="small"
                              label={`Combo · ${combo.length}`}
                              color="primary"
                              variant="outlined"
                              sx={{ fontWeight: 700 }}
                            />
                          );
                        }
                        return null;
                      })()}
                      <Chip
                        size="small"
                        label={st}
                        color={
                          st === 'active'
                            ? 'success'
                            : st === 'paused'
                              ? 'warning'
                              : 'default'
                        }
                        sx={{ fontWeight: 700, textTransform: 'capitalize' }}
                      />
                      {canPay(c) && (
                        <>
                          <Button
                            size="small"
                            variant="contained"
                            startIcon={
                              payingId === c.id ? (
                                <CircularProgress size={14} color="inherit" />
                              ) : (
                                <PaymentIcon />
                              )
                            }
                            disabled={!!payingId || !!actionId}
                            onClick={() => payCampaign(c.id)}
                          >
                            Pay with Stripe
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={
                              payingId === `wallet-${c.id}` ? (
                                <CircularProgress size={14} color="inherit" />
                              ) : (
                                <AccountBalanceWalletIcon />
                              )
                            }
                            disabled={!!payingId || !!actionId}
                            onClick={() => payFromWallet(c.id)}
                          >
                            Pay from wallet
                          </Button>
                          <Button size="small" component={RouterLink} to="/topup" variant="text">
                            Top up wallet
                          </Button>
                        </>
                      )}
                      {canDelete(c) && (
                        <Button
                          size="small"
                          color="error"
                          startIcon={<DeleteOutlineIcon />}
                          disabled={!!actionId || !!payingId}
                          onClick={() => runAction(c.id, 'delete')}
                        >
                          Delete
                        </Button>
                      )}
                      {rawStatus === 'active' && st !== 'completed' && (
                        <>
                          <Button
                            size="small"
                            startIcon={<PauseIcon />}
                            disabled={!!actionId}
                            onClick={() => runAction(c.id, 'pause')}
                          >
                            Pause
                          </Button>
                          <Button
                            size="small"
                            color="warning"
                            startIcon={<StopIcon />}
                            disabled={!!actionId}
                            onClick={() => runAction(c.id, 'stop')}
                          >
                            Stop
                          </Button>
                        </>
                      )}
                      {rawStatus === 'paused' && st !== 'completed' && (
                        <>
                          <Button
                            size="small"
                            startIcon={<PlayArrowIcon />}
                            disabled={!!actionId}
                            onClick={() => runAction(c.id, 'resume')}
                          >
                            Resume
                          </Button>
                          <Button
                            size="small"
                            color="warning"
                            startIcon={<StopIcon />}
                            disabled={!!actionId}
                            onClick={() => runAction(c.id, 'stop')}
                          >
                            Stop
                          </Button>
                        </>
                      )}
                    </Stack>
                  </Stack>
                </Paper>
              );
            })}
          </Stack>
        )}
      </Paper>
    </Box>
  );
}
